<template>
  <div
    v-if="isOpen"
    class="fixed inset-0 z-50 overflow-y-auto"
    aria-labelledby="modal-title"
    role="dialog"
    aria-modal="true"
  >
    <!-- Background overlay -->
    <div
      class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
      @click="handleClose"
    ></div>

    <!-- Modal panel -->
    <div
      class="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0"
    >
      <div
        class="relative transform overflow-hidden rounded-lg bg-white dark:bg-slate-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl sm:p-6"
      >
        <!-- Header -->
        <div class="flex items-center justify-between mb-6">
          <div class="flex items-center">
            <div
              class="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-green-100 dark:bg-green-900"
            >
              <vue-feather
                type="upload"
                size="24"
                class="text-green-600 dark:text-green-200"
              />
            </div>
            <div class="ml-4">
              <h3
                class="text-lg font-semibold leading-6 text-gray-900 dark:text-white"
                id="modal-title"
              >
                Save to EBM
              </h3>
              <p class="text-sm text-gray-500 dark:text-gray-400">
                Review and confirm the data that will be sent to RRA EBM
              </p>
            </div>
          </div>
          <button
            @click="handleClose"
            class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <vue-feather type="x" size="24" />
          </button>
        </div>

        <!-- Loading state -->
        <div v-if="loading" class="flex justify-center items-center py-8">
          <vue-feather
            type="loader"
            size="32"
            class="animate-spin text-blue-600"
          />
          <span class="ml-2 text-gray-600 dark:text-gray-300"
            >Loading EBM settings...</span
          >
        </div>

        <!-- Content -->
        <div v-else class="space-y-6">
          <!-- Item Information -->
          <div class="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
            <h4 class="text-md font-medium text-gray-900 dark:text-white mb-3">
              Item Information
            </h4>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label
                  class="text-sm font-medium text-gray-600 dark:text-gray-300"
                  >Name</label
                >
                <p class="text-sm text-gray-900 dark:text-white">
                  {{ item?.name || "N/A" }}
                </p>
              </div>
              <div>
                <label
                  class="text-sm font-medium text-gray-600 dark:text-gray-300"
                  >SKU</label
                >
                <p class="text-sm text-gray-900 dark:text-white">
                  {{ item?.sku || "N/A" }}
                </p>
              </div>
              <div v-if="item?.barcode">
                <label
                  class="text-sm font-medium text-gray-600 dark:text-gray-300"
                  >Barcode</label
                >
                <p class="text-sm text-gray-900 dark:text-white">
                  {{ item.barcode }}
                </p>
              </div>
              <div v-if="item?.description">
                <label
                  class="text-sm font-medium text-gray-600 dark:text-gray-300"
                  >Description</label
                >
                <p class="text-sm text-gray-900 dark:text-white">
                  {{ item.description }}
                </p>
              </div>
            </div>
          </div>

          <!-- EBM Data Preview -->
          <div class="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
            <h4 class="text-md font-medium text-gray-900 dark:text-white mb-3">
              Data to be sent to RRA EBM
            </h4>

            <!-- Product/Service specific fields -->
            <div class="space-y-4">
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label
                    class="text-sm font-medium text-gray-600 dark:text-gray-300"
                    >Item Code</label
                  >
                  <p class="text-sm text-gray-900 dark:text-white">
                    {{ ebmData.itemCd }}
                  </p>
                </div>
                <div>
                  <label
                    class="text-sm font-medium text-gray-600 dark:text-gray-300"
                    >Item Classification Code</label
                  >
                  <p class="text-sm text-gray-900 dark:text-white">
                    {{ ebmData.itemClsCd }}
                  </p>
                </div>
                <div>
                  <label
                    class="text-sm font-medium text-gray-600 dark:text-gray-300"
                    >Item Type Code</label
                  >
                  <p class="text-sm text-gray-900 dark:text-white">
                    {{ ebmData.itemTyCd }}
                  </p>
                </div>
                <div>
                  <label
                    class="text-sm font-medium text-gray-600 dark:text-gray-300"
                    >Origin Place Code</label
                  >
                  <p class="text-sm text-gray-900 dark:text-white">
                    {{ ebmData.orgnNatCd }}
                  </p>
                </div>
                <div>
                  <label
                    class="text-sm font-medium text-gray-600 dark:text-gray-300"
                    >Packaging Unit Code</label
                  >
                  <p class="text-sm text-gray-900 dark:text-white">
                    {{ ebmData.pkgUnitCd }}
                  </p>
                </div>
                <div>
                  <label
                    class="text-sm font-medium text-gray-600 dark:text-gray-300"
                    >Quantity Unit Code</label
                  >
                  <p class="text-sm text-gray-900 dark:text-white">
                    {{ ebmData.qtyUnitCd }}
                  </p>
                </div>
                <div>
                  <label
                    class="text-sm font-medium text-gray-600 dark:text-gray-300"
                    >Taxation Type Code</label
                  >
                  <p class="text-sm text-gray-900 dark:text-white">
                    {{ ebmData.taxTyCd }}
                  </p>
                </div>
                <div>
                  <label
                    class="text-sm font-medium text-gray-600 dark:text-gray-300"
                    >Sale Unit Price</label
                  >
                  <p class="text-sm text-gray-900 dark:text-white">
                    {{ ebmData.dftPrc }}
                  </p>
                </div>
              </div>

              <!-- Group Prices -->
              <div class="border-t border-gray-200 dark:border-gray-600 pt-4">
                <h5
                  class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                  Group Prices
                </h5>
                <div class="grid grid-cols-2 md:grid-cols-5 gap-2">
                  <div
                    v-for="(price, index) in groupPrices"
                    :key="index"
                    class="text-center"
                  >
                    <label class="text-xs text-gray-500 dark:text-gray-400"
                      >Level {{ index + 1 }}</label
                    >
                    <p
                      class="text-sm font-medium text-gray-900 dark:text-white"
                    >
                      {{ price }}
                    </p>
                  </div>
                </div>
              </div>

              <!-- Safety Quantity -->
              <div>
                <label
                  class="text-sm font-medium text-gray-600 dark:text-gray-300"
                  >Safety Quantity</label
                >
                <p class="text-sm text-gray-900 dark:text-white">
                  {{ ebmData.sftyQty }}
                </p>
              </div>
            </div>
          </div>

          <!-- Warning Message -->
          <div
            class="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4"
          >
            <div class="flex">
              <div class="flex-shrink-0">
                <vue-feather
                  type="alert-triangle"
                  class="h-5 w-5 text-yellow-400"
                />
              </div>
              <div class="ml-3">
                <h3
                  class="text-sm font-medium text-yellow-800 dark:text-yellow-200"
                >
                  Important Notice
                </h3>
                <div class="mt-2 text-sm text-yellow-700 dark:text-yellow-300">
                  <p>
                    This action will send the item data to RRA EBM system.
                    Please ensure all information is correct before proceeding.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Actions -->
        <div class="mt-6 flex justify-end gap-3">
          <button
            type="button"
            class="rounded-md bg-white dark:bg-slate-700 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-slate-600"
            @click="handleClose"
          >
            Cancel
          </button>
          <button
            type="button"
            :disabled="loading"
            class="rounded-md bg-green-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600 disabled:opacity-50"
            @click="handleConfirm"
          >
            <span v-if="loading" class="flex items-center">
              <vue-feather type="loader" size="16" class="animate-spin mr-2" />
              Saving...
            </span>
            <span v-else>Save to EBM</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "SaveToEbmModal",
  props: {
    isOpen: {
      type: Boolean,
      required: true,
    },
    item: {
      type: Object,
      default: null,
    },
    activeTab: {
      type: String,
      required: true,
    },
    ebmSettings: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ebmData() {
      if (!this.item || !this.ebmSettings) return {};

      // Map frontend data to RRA EBM format
      return {
        item_id: this.item.id,
        tin: this.ebmSettings?.tin,
        itemCd: this.item.sku || this.generateItemCode(),
        itemClsCd: this.getDefaultItemClass(),
        itemTyCd: this.getDefaultItemType(),
        itemNm: this.item.name,
        orgnNatCd: this.getDefaultOriginCode(),
        pkgUnitCd: this.getDefaultPackageUnit(),
        qtyUnitCd: this.item.quantity_unit || "U",
        taxTyCd: this.getDefaultTaxType(),
        dftPrc: this.extractPrice(this.item.price),
        grpPrcL1: this.extractPrice(this.item.price),
        grpPrcL2: this.extractPrice(this.item.price),
        grpPrcL3: this.extractPrice(this.item.price),
        grpPrcL4: this.extractPrice(this.item.price),
        grpPrcL5: this.extractPrice(this.item.price),
        sftyQty: 0,
        isrcAplcbYn: "N",
        useYn: "Y",
        bcd: this.item.barcode || "",
        addInfo: this.item.description || "",
      };
    },
    groupPrices() {
      if (!this.item) return [0, 0, 0, 0, 0];
      const price = this.extractPrice(this.item.price);
      return [price, price, price, price, price];
    },
  },
  methods: {
    handleClose() {
      this.$emit("close");
    },
    handleConfirm() {
      this.$emit("confirm", this.ebmData);
    },
    generateItemCode() {
      // Generate a unique item code based on SKU or name
      if (!this.item) return "ITEM_" + Date.now();
      const base =
        this.item.sku ||
        this.item.name?.replace(/\s+/g, "").toUpperCase() ||
        "ITEM";
      return base.substring(0, 20); // RRA EBM limit
    },
    getDefaultItemClass() {
      // Default item classification - this should be configurable
      return "001";
    },
    getDefaultItemType() {
      // Default item type - this should be configurable
      return this.activeTab === "services" ? "SERV" : "GOOD";
    },
    getDefaultOriginCode() {
      // Default origin code - this should be configurable
      return "RW";
    },
    getDefaultPackageUnit() {
      // Default package unit - this should be configurable
      return "PCS";
    },
    getDefaultTaxType() {
      // Default tax type - this should be configurable
      return "B"; // 18% VAT
    },
    extractPrice(priceString) {
      if (!priceString) return 0;
      // Extract numeric value from price string like "RWF 1,234.56"
      const match = priceString.match(/[\d,]+\.?\d*/);
      if (match) {
        return parseFloat(match[0].replace(/,/g, ""));
      }
      return 0;
    },
  },
};
</script>

<style scoped>
/* Add any custom styles here */
</style>
